import { Button, ButtonsGroup, Modal, ModalProps, Space, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useSegment } from '../../../../hooks'
import type { QuoteConversionTrackingOption } from '../../types/quotes/quoteConversionTrackingOption'

interface MarkQuoteAsAcceptedConfirmationModalProps extends ModalProps {
  onConfirm: () => void
  trackingOption?: QuoteConversionTrackingOption
}

export const MarkQuoteAsAcceptedConfirmationModal = ({
  onConfirm,
  trackingOption,
  ...modalProps
}: MarkQuoteAsAcceptedConfirmationModalProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { close: closeModal } = useModal(modalProps.id)

  const handleReturnButtonClick = useCallback(() => {
    track('XXX - Invoicing Core - Quote View - Mark quote as accepted confirmation modal - Return button clicked', {
      ...(trackingOption ? { conversionTo: trackingOption } : {}),
    })
    closeModal()
  }, [closeModal, track, trackingOption])

  const handleConfirmButtonClick = useCallback(() => {
    track('XXX - Invoicing Core - Quote View - Mark quote as accepted confirmation modal - Confirm button clicked', {
      ...(trackingOption ? { conversionTo: trackingOption } : {}),
    })
    onConfirm()
  }, [onConfirm, track, trackingOption])

  return (
    <Modal {...modalProps} closable size="s">
      <Modal.Header title={t('external_invoices.convert_quote_to_deposit_invoice_confirmation_modal.title')} />
      <Space size="s16" />
      <Modal.Body>
        <Text>
          <Trans i18nKey="external_invoices.convert_quote_to_deposit_invoice_confirmation_modal.description">
            Please make sure your client has agreed beforehand.
            <strong>Once accepted, a quote can no longer be edited.</strong>
          </Trans>
        </Text>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button variant="tonal" onClick={handleReturnButtonClick}>
            {t('return')}
          </Button>
          <Button onClick={handleConfirmButtonClick}>{t('confirm')}</Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
